'use strict'

// A reference to another spec, in the two spellings the spec board already
// understands: "owner/repo#12" across namespaces, "#12" within the note's own.
// The trailing guard keeps hex colours (#12ab34) and "#12-suffix" out; the
// leading one keeps "##12" and "word#12" out.
const REF_RE = /(^|[^\w#/-])((?:[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+)?#(\d{1,6}))(?![\w-])/g
const NS_RE = /^[\w.-]+\/[\w.-]+$/

// YAML gives whatever the author wrote, and constrainObject hands on maps with
// a null prototype, which String() throws on. Scalars only, the same rule
// HedgeDoc's own Note.parseMeta applies to frontmatter it trusts.
function scalar (value) {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : ''
}

function normList (value) {
  if (value == null) return []
  const raw = Array.isArray(value) ? value : scalar(value).split(',')
  return raw.map(scalar).filter(Boolean)
}

// Only the board maps a spec number to a note, so a reference addresses it there
// and the board redirects. Keeps this side synchronous: no fetch, no cache.
function specRefUrl (ns, n, boardURL) {
  if (!boardURL || !NS_RE.test(String(ns))) return null
  return String(boardURL).replace(/\/+$/, '') + '/spec/' + ns + '/' + n
}

// A reference as frontmatter spells it, which is looser than prose: a bare
// number is allowed (YAML reads an unquoted leading "#" as a comment, so it is
// the spelling the docs recommend), and a note shortid addresses a spec that
// has no number yet. Mirrors the board's specRef.
function parseMetaRef (value, defaultNs) {
  const v = scalar(value)
  if (!v) return null
  const m = /^(?:([\w.-]+\/[\w.-]+)#)?#?(\d{1,6})$/.exec(v)
  if (m) {
    const n = Number(m[2])
    const ns = m[1] || defaultNs
    return n && ns && NS_RE.test(ns) ? { label: '#' + n, ns, n } : null
  }
  // Length-bounded like the /new/spec route's own check, so a placeholder such
  // as "none" or "tbd" is left as text instead of linking to a note that is not
  // there.
  if (/^[A-Za-z0-9_-]{6,}$/.test(v)) return { label: v, noteId: v }
  return null
}

// A reference whose namespace is neither spelled out nor inferable stays text.
function splitSpecRefs (text, defaultNs) {
  const out = []
  let last = 0
  // matchAll clones the regex, so the module-level lastIndex is never touched.
  for (const m of text.matchAll(REF_RE)) {
    const [full, lead, ref, digits] = m
    const n = Number(digits)
    const ns = ref.includes('/') ? ref.slice(0, ref.indexOf('#')) : defaultNs
    // A rejected match stays text: last is left where it is, so the next plain
    // segment swallows it.
    if (!n || !ns || !NS_RE.test(ns)) continue
    const start = m.index + lead.length
    if (start > last) out.push({ text: text.slice(last, start) })
    out.push({ text: ref, ns, n })
    last = m.index + full.length
  }
  if (out.length && last < text.length) out.push({ text: text.slice(last) })
  return out
}

// Tags whose text is not prose. Headings are excluded because postProcess runs
// before deduplicatedHeaderId, which derives every heading id from its
// innerHTML: linking inside one would silently repoint the TOC and break
// existing deep links.
const SKIP = /^(A|CODE|PRE|KBD|SAMP|H1|H2|H3|H4|H5|H6)$/

// Anchors are built from validated captures rather than parsed from a string,
// which matters because postProcess runs after preventXSS.
function linkifySpecRefs (root, defaultNs, boardURL) {
  if (!root || !boardURL) return
  const doc = root.ownerDocument
  // FILTER_REJECT on an element prunes its whole subtree, so a code block costs
  // one visit instead of one per text node inside it.
  const walker = doc.createTreeWalker(root, 5 /* SHOW_ELEMENT | SHOW_TEXT */, {
    acceptNode: n => n.nodeType === 1
      ? (SKIP.test(n.tagName) ? 2 /* REJECT */ : 3 /* SKIP */)
      : 1 /* ACCEPT */
  })
  const targets = []
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeValue.indexOf('#') !== -1) targets.push(node)
  }
  for (const node of targets) {
    const parts = splitSpecRefs(node.nodeValue, defaultNs)
    if (!parts.length) continue
    const frag = doc.createDocumentFragment()
    for (const part of parts) {
      if (!part.ns) { frag.appendChild(doc.createTextNode(part.text)); continue }
      const a = doc.createElement('a')
      a.className = 'spec-ref'
      a.href = specRefUrl(part.ns, part.n, boardURL)
      a.title = 'Spec ' + part.ns + '#' + part.n
      a.textContent = part.text
      frag.appendChild(a)
    }
    node.parentNode.replaceChild(frag, node)
  }
}

module.exports = { NS_RE, scalar, normList, specRefUrl, parseMetaRef, splitSpecRefs, linkifySpecRefs }
